import { Stack, Paper, Group, Title, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportFieldsForm } from '../../components/import-fields-form/import-fields-form';
import SeedBatchesTable from '../../components/seed-batches-table/seed-batches-table';
import {
  contractsApi,
  BatchesImportFileParams,
} from '../../lib/apis/contracts';

import { useSharedQueries, FORM_STATUS } from '@data-access';
import { Drawer } from '@ui';

export function BatchesPage() {
  const { t } = useTranslation();
  const { contractId } = useParams({
    from: '/_app/os/contracts_/$contractId/batches',
  });
  const [opened, actions] = useDisclosure(false);
  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);

  const queryClient = useQueryClient();
  const sharedQueries = useSharedQueries();
  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });
  const importFields = useMutation({
    mutationFn: contractsApi.batches.importFile,
  });

  const exportFields = useMutation({
    mutationFn: contractsApi.batches.exportFile,
  });

  const handleSubmit = (
    values: Omit<BatchesImportFileParams, 'contractId'>,
  ) => {
    importFields.mutate(
      {
        ...values,
        contractId: Number(contractId),
      },
      {
        onSuccess: (data) => {
          setComputationRequestId(data.async_computation_request_id);
          actions.close();
        },
      },
    );
  };

  const handleExport = (
    values: Omit<BatchesImportFileParams, 'contractId' | 'file'>,
  ) => {
    exportFields.mutate({
      ...values,
      contractId: Number(contractId),
    });
  };

  useEffect(() => {
    if (computationRequest.data?.status === FORM_STATUS.FINISHED) {
      queryClient.invalidateQueries();
      setComputationRequestId(null);
    } else if (computationRequest.data?.status === FORM_STATUS.ERROR) {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.import-fields-error'),
        color: 'red',
      });
    }
  }, [computationRequest.data, queryClient, t]);

  return (
    <Stack flex={1} h="100%" style={{ overflow: 'hidden' }}>
      <Paper>
        <Group justify="space-between">
          <Title order={2}>{t('common.lots-de-graines')}</Title>
          {/* <Button
            loading={computationRequest.data?.status === FORM_STATUS.PENDING}
            onClick={actions.open}
          >
            {t('common.importer-des-parcelles')}
          </Button> */}
        </Group>
      </Paper>
      <SeedBatchesTable contractId={Number(contractId)} />

      <Drawer
        onClose={actions.close}
        opened={opened}
        position="right"
        title={t('common.importer-des-parcelles')}
      >
        <ImportFieldsForm
          onCancel={actions.close}
          onExport={handleExport}
          onSubmit={handleSubmit}
          pending={importFields.isPending}
        />
      </Drawer>
    </Stack>
  );
}
